import { Button, Checkbox, DatePicker, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import { getDDMMYYYFormat } from "../../../../helper/dateTimeHelper";
import { Table } from "../../../../components";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import "./index.scss";
import { Moment } from "moment";
import { useSearchParams } from "../../../../hook/use-search-params";
import { TableHelpers } from "../../../../helper/TableHelper";
import { useState } from "react";
import dayjs from "dayjs";
import { usePublishDocumentsInfo } from "../../../statistics/hooks/use-publish-document-info";

type RangeValue = [Moment | null, Moment | null] | null;

const { RangePicker } = DatePicker;

export const UserDocumentTableAdmin = () => {
  const { replaceSearchParams, searchParams } = useSearchParams();
  const [isColunmVisible, setColunmVisible] = useState<boolean>(false);

  const {
    filteredData,
    countExternalDocument,
    countInternalDocument,
    countSQMSDocument,
    isLoading,
  } = usePublishDocumentsInfo();

  const startStringDate = searchParams.get("start");
  const endStringDate = searchParams.get("end");

  const dateStartFormated = dayjs(startStringDate).format("DD-MM-YYYY");
  const dateEndFormated = dayjs(endStringDate).format("DD-MM-YYYY");

  const titleOfExcelFile =
    startStringDate && endStringDate
      ? `Danh sách tài liệu đã triển khai trong kỳ thời gian từ ${dateStartFormated} đến ${dateEndFormated}`
      : "Danh sách tài liệu đã triển khai trong kỳ thời gian từ.............đến............";

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();

    let result: any = filteredData?.map((dbp: any) => {
      const percentage = ((dbp.docReaders?.length ?? 0) / (dbp.docPublicEndUsers?.length ?? 1)) * 100;
      const unreadPercentage = ((dbp.docPublicEndUsers?.length ?? 0) - (dbp.docReaders?.length ?? 0)) /(dbp.docPublicEndUsers?.length ?? 1) * 100;
      return {
        docCode: dbp.docCode,
        Title: dbp.Title,
        docType: dbp.docType,
        docStatus: dbp.docStatus,
        effectiveFrom: getDDMMYYYFormat(dbp.effectiveFrom),
        quantityRead: dbp.docReaders?.length ?? 0,
        readedPercent: percentage % 1 === 0 ? percentage + "%" : percentage.toFixed(2) + "%",
        quantityUnread: (dbp.docPublicEndUsers?.length ?? 0) - (dbp.docReaders?.length ?? 0),
        unreadPercent: unreadPercentage % 1 === 0 ? unreadPercentage + "%" : unreadPercentage.toFixed(2) + "%",
        createdDateTime: getDDMMYYYFormat(dbp.createdDateTime),
        effectiveTo: getDDMMYYYFormat(dbp.effectiveTo)
      };
    });

    const exportContent = {
      fileName: "DanhSachPhanPhoiTaiLieu.xlsx",
      sheet: "Sheet 1",
      content: [
        { header: "Mã tài liệu", dataIndex: "docCode" },
        { header: "Tên tài liệu", dataIndex: "Title" },
        { header: "Loại tài liệu", dataIndex: "docType" },
        { header: "Tình Trạng", dataIndex: "docStatus" },
        { header: "Ngày hiệu lực", dataIndex: "effectiveFrom" },
        { header: "SL đã đọc", dataIndex: "quantityRead" },
        { header: "Tỉ lệ đã đọc", dataIndex: "readedPercent" },
        { header: "SL chưa đọc", dataIndex: "quantityUnread" },
        { header: "Tỉ lệ chưa đọc", dataIndex: "unreadPercent" },
        { header: "Ngày ban hành", dataIndex: "createdDateTime" },
        { header: "Ngày hết hiệu lực", dataIndex: "effectiveTo" },
      ],
    };

    const worksheet = workbook.addWorksheet(exportContent.sheet);

    //* Defind colunms based on export content
    worksheet.mergeCells("A1:K2");
    worksheet.getCell("A1").value = titleOfExcelFile;
    worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
    worksheet.getCell("A1").font = { bold: true, size: 14 };

    //* Set header value row
    const headerRow = worksheet.getRow(3);
    headerRow.values = exportContent.content.map((col) => col.header);
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: "center" };

    //* Add data row
    exportContent.content.forEach((_col, index) => {
      worksheet.getCell(3, index + 1).font = { bold: true };
    });

    //* Add data rows
    result.forEach((data: any) => {
      const row = exportContent.content.map((col) => data[col.dataIndex]);
      const dataRow = worksheet.addRow(row);

      dataRow.font = { bold: false };
    });

    worksheet.columns.forEach((column) => {
      if (column && column.eachCell) {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
          const cellLength = cell.value ? cell.value.toString().length : 10;
          if (cellLength > maxLength) {
            maxLength = cellLength;
          }
        });
        column.width = Math.min(maxLength + 2, 30);
      }
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), exportContent.fileName);
  };

  const columns: ColumnsType<any> = [
    {
      title: "Mã tài liệu",
      dataIndex: "docCode",
      key: "docCode",
      align: "center" as const,
      width: 120,
      ...TableHelpers.searchInput({ dataIndex: "docCode" }),
    },
    {
      title: "Tên tài liệu",
      dataIndex: "Title",
      key: "Title",
      ...TableHelpers.searchInput({ dataIndex: "Title" }),
    },
    {
      title: "Loại tài liệu",
      dataIndex: "docType",
      key: "docType",
      filters: [
        {
          text: "NỘI BỘ",
          value: "NỘI BỘ",
        },
        {
          text: "BÊN NGOÀI",
          value: "BÊN NGOÀI",
        },
        {
          text: "TÀI LIỆU SQMS",
          value: "TÀI LIỆU SQMS"
        }
      ],
      onFilter: (status: any, record) => record.docType.indexOf(status) === 0,
    },
    {
      title: "Tình Trạng",
      dataIndex: "docStatus",
      key: "docStatus",
    },
    {
      title: "Ngày hiệu lực",
      dataIndex: "effectiveFrom",
      key: "effectiveFrom",
      render: (date: string) => getDDMMYYYFormat(date),
      ...TableHelpers.searchRangePicker({ dataIndex: "effectiveFrom" }),
    },

    {
      title: "SL đã đọc",
      dataIndex: "quantityRead",
      key: "quantityRead",
      render: (isRead: any, record: any) => record.docReaders?.length ?? 0,
      align: "center" as const,
    },
    {
      title: "Tỉ lệ đã đọc",
      key: "readedPercent",
      render: (data: { quantityRead: number; quantityUnread: number }, record: any): string => {
        const percentage = ((record.docReaders?.length ?? 0) / (record.docPublicEndUsers?.length ?? 1)) * 100;
        if (percentage % 1 === 0) {
          return percentage + "%";
        } else {
          return percentage.toFixed(2) + "%";
        }
      },
      align: "center" as const,
    },
    {
      title: "SL chưa đọc",
      dataIndex: "quantityUnread",
      key: "quantityUnread",
      render: (isRead: any, record: any) => (record.docPublicEndUsers?.length ?? 0) - (record.docReaders?.length ?? 0),
      align: "center" as const,
    },
    {
      title: "Tỉ lệ chưa đọc",
      key: "unreadPercent",
      render: (data: { quantityRead: number; quantityUnread: number }, record: any): string => {
        const percentage = ((record.docPublicEndUsers?.length ?? 0) - (record.docReaders?.length ?? 0)) /(record.docPublicEndUsers?.length ?? 1) * 100;
        if (percentage % 1 === 0) {
          return percentage + "%";
        } else {
          return percentage.toFixed(2) + "%";
        }
      },
      align: "center" as const,
    },
    {
      title: "Ngày ban hành",
      dataIndex: "createdDateTime",
      key: "createdDateTime",

      render: (date: string) => getDDMMYYYFormat(date),
      ...TableHelpers.searchRangePicker({ dataIndex: "createdDateTime" }),
      className: `${isColunmVisible ? "block" : "d-none"}`,
    },
    {
      title: "Ngày hết hiệu lực",
      dataIndex: "effectiveTo",
      render: (date: string) => getDDMMYYYFormat(date),
      key: "effectiveTo",
      ...TableHelpers.searchRangePicker({ dataIndex: "effectiveTo" }),
      className: `${isColunmVisible ? "block" : "d-none"}`,
    },
  ];

  const handleDateChange = (value: RangeValue) => {
    if (value) {
      const [startDate, endDate] = value;
      searchParams.set("start", startDate?.startOf("month").startOf("day")?.toISOString() || "");
      searchParams.set("end", endDate?.endOf("month").endOf("day")?.toISOString() || "");
      replaceSearchParams(searchParams);
    } else {
      searchParams.delete("start");
      searchParams.delete("end");
      replaceSearchParams(searchParams);
    }
  };

  const handleRowClick = (record: any) => {
    searchParams.set("docId", record.docWorkingId);
    replaceSearchParams(searchParams);
  };

  return (
    <>
      <div className="modal-title">
        <div className="modal-left">
          <div>
            <h5 style={{ color: "#00a9c4" }}>Bảng thống kê danh sách tài liệu ban hành cho cơ quan đơn vị</h5>
          </div>
          <div className="d-flex column">
            <p className="mx-2">
              <span style={{ fontWeight: "bold" }}>Tài liệu đã triển khai: </span>
            </p>
          </div>
          <div className="d-flex column">
            <p className="mx-2">
              Tổng cộng: <span>{filteredData?.length}</span>
            </p>
            <p className="mx-2">
              SL Tài liệu nội bộ: <span>{countInternalDocument}</span>
            </p>
            <p className="mx-2">
              SL Tài liệu bên ngoài: <span>{countExternalDocument}</span>
            </p>
            <p className="mx-2">
              SL Tài liệu SQMS: <span>{countSQMSDocument}</span>
            </p>
          </div>
        </div>
        <div className="modal-right">
          <div className="py-2 modal-date-picker">
            <Typography.Text className="modal-date-text">Thời gian ban hành/triển khai</Typography.Text>
            <RangePicker
              placeholder={["Ngày bắt đầu", "Ngày kết thúc"]}
              style={{ width: "58%", height: "40px" }}
              onChange={handleDateChange}
            />
          </div>
          <Button type="primary" onClick={handleExport}>
            Xuất excel
          </Button>
        </div>
      </div>

      <div className="px-0 pb-0 modal-body">
        <div className="d-flex justify-content-end">
          <Typography.Text className="mb-1 mr-3">Hiện thêm cột</Typography.Text>
          <Checkbox checked={isColunmVisible} onChange={(value) => setColunmVisible(value.target.checked)} />
        </div>
        <Table
          rowKey="id"
          columns={columns}
          loading={isLoading}
          showHeader={true}
          dataSource={filteredData}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: "pointer" },
          })}
          pagination={{
            showTotal: (total) => `Tổng cộng: ${total}`,
            position: ["bottomRight"],
          }}
        />
      </div>
    </>
  );
};
